'use client'

import { useEffect, useRef, useState } from 'react'
import dynamic from 'next/dynamic'
import { useLocale } from 'next-intl'
import { type Locale } from '@/i18n/routing'
import { AppIcon } from '@/components/ui/icons'
import { usePathname, useRouter } from '@/i18n/navigation'

const ConfirmDialog = dynamic(() => import('./ConfirmDialog'), { ssr: false })

const LANGUAGE_LABELS: Record<Locale, string> = {
    zh: '简体中文',
    en: 'English',
}

const SWITCH_CONFIRM_COPY: Record<Locale, { title: string; message: string; action: string; cancel: string; triggerLabel: string }> = {
    zh: {
        title: '切换语言？',
        message:
            '切换到 {targetLanguage} 后，不仅界面文字会改变，整条流程的提示词模板、剧本生成和任务输出语言也会同步切换。是否继续？',
        action: '确认切换',
        cancel: '取消',
        triggerLabel: '切换语言',
    },
    en: {
        title: 'Switch language?',
        message:
            'Switching to {targetLanguage} will update not only interface text, but also end-to-end prompt templates, script generation, and workflow output language. Continue?',
        action: 'Switch now',
        cancel: 'Cancel',
        triggerLabel: 'Switch language',
    },
}

function isSupportedLocale(locale?: string): locale is Locale {
    return locale === 'zh' || locale === 'en'
}

export default function LanguageSwitcher() {
    const router = useRouter()
    const pathname = usePathname()
    const locale = useLocale()
    const containerRef = useRef<HTMLDivElement | null>(null)
    const [isMenuOpen, setIsMenuOpen] = useState(false)
    const [showConfirm, setShowConfirm] = useState(false)
    const [pendingLocale, setPendingLocale] = useState<Locale | null>(null)

    if (!isSupportedLocale(locale)) {
        throw new Error('LanguageSwitcher requires locale to be zh or en')
    }
    const currentLocale: Locale = locale
    const targetLocale: Locale = currentLocale === 'zh' ? 'en' : 'zh'
    const activeLocaleForCopy: Locale = pendingLocale ?? targetLocale
    const confirmCopy = SWITCH_CONFIRM_COPY[activeLocaleForCopy]
    const menuLabel = currentLocale === 'zh' ? '界面语言' : 'Interface language'

    useEffect(() => {
        if (!isMenuOpen) return

        const handleClickOutside = (event: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
                setIsMenuOpen(false)
            }
        }

        document.addEventListener('mousedown', handleClickOutside)
        return () => {
            document.removeEventListener('mousedown', handleClickOutside)
        }
    }, [isMenuOpen])

    const requestLanguageSwitch = (newLocale: Locale) => {
        setIsMenuOpen(false)
        if (newLocale === currentLocale) return
        setPendingLocale(newLocale)
        setShowConfirm(true)
    }

    const confirmLanguageSwitch = () => {
        if (!pendingLocale) {
            throw new Error('LanguageSwitcher confirm requires a pending locale')
        }
        setShowConfirm(false)
        setPendingLocale(null)
        router.replace(pathname, { locale: pendingLocale })
    }

    const cancelLanguageSwitch = () => {
        setShowConfirm(false)
        setPendingLocale(null)
    }

    return (
        <>
            <div ref={containerRef} className="relative inline-block">
                <button
                    type="button"
                    onClick={() => setIsMenuOpen((prev) => !prev)}
                    aria-label={SWITCH_CONFIRM_COPY[targetLocale].triggerLabel}
                    aria-expanded={isMenuOpen}
                    className="inline-flex h-11 items-center gap-2 rounded-full border border-white/22 bg-[rgba(255,255,255,0.44)] px-4 text-sm font-semibold text-[var(--glass-text-primary)] shadow-[0_4px_14px_rgba(18,24,39,0.04)] backdrop-blur-[18px] transition hover:bg-[rgba(255,255,255,0.66)]"
                >
                    <AppIcon name="globe" className="h-4 w-4" />
                    <span>{LANGUAGE_LABELS[currentLocale]}</span>
                    <AppIcon name="chevronDown" className="h-4 w-4 text-[var(--glass-text-tertiary)]" />
                </button>

                {isMenuOpen ? (
                    <div className="absolute right-0 z-50 mt-2 w-[220px] rounded-[28px] border border-white/28 bg-[linear-gradient(180deg,rgba(255,255,255,0.94)_0%,rgba(241,246,255,0.88)_100%)] p-2.5 shadow-[0_24px_60px_rgba(18,24,39,0.12)] backdrop-blur-[24px]">
                        <div className="px-3 pb-1 pt-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-[var(--glass-text-tertiary)]">
                            {menuLabel}
                        </div>
                        {(Object.entries(LANGUAGE_LABELS) as Array<[Locale, string]>).map(([locale, label]) => {
                            const isActive = locale === currentLocale
                            return (
                                <button
                                    key={locale}
                                    type="button"
                                    onClick={() => requestLanguageSwitch(locale)}
                                    className={`mt-1 flex w-full items-center justify-between rounded-[20px] px-3.5 py-3 text-left text-sm font-medium transition-colors ${isActive
                                        ? 'bg-[rgba(79,109,255,0.08)] text-[var(--glass-text-primary)]'
                                        : 'text-[var(--glass-text-secondary)] hover:bg-white/75 hover:text-[var(--glass-text-primary)]'
                                        }`}
                                >
                                    <span>{label}</span>
                                    {isActive ? <AppIcon name="check" className="h-4 w-4 text-[var(--glass-tone-info-fg)]" /> : null}
                                </button>
                            )
                        })}
                    </div>
                ) : null}
            </div>
            {showConfirm ? (
                <ConfirmDialog
                    show={showConfirm}
                    title={confirmCopy.title}
                    message={confirmCopy.message.replace('{targetLanguage}', pendingLocale ? LANGUAGE_LABELS[pendingLocale] : '')}
                    confirmText={confirmCopy.action}
                    cancelText={confirmCopy.cancel}
                    onConfirm={confirmLanguageSwitch}
                    onCancel={cancelLanguageSwitch}
                    type="info"
                />
            ) : null}
        </>
    )
}
